import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiCall, ApiError } from '../utils/apiHelpers';
import type {
  FieldValue,
  ParticipantDoc,
  QrGenerateResponse,
  SchemaDoc,
  SchemaProperty,
} from '../types';

type FieldDraft = Record<string, FieldValue>;

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem',
  border: '1px solid var(--border-color)',
  borderRadius: 4,
  background: 'var(--card-bg)',
  color: 'var(--text-color)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 4,
};

function emptyDraft(schema: SchemaDoc): FieldDraft {
  const draft: FieldDraft = {};
  for (const p of schema.properties) {
    draft[p.name] = p.type === 'boolean' ? false : '';
  }
  return draft;
}

export function GenerateQRPage() {
  const { industryId, schemaId } = useParams<{ industryId: string; schemaId: string }>();
  const [searchParams] = useSearchParams();
  const participantId = searchParams.get('participantId');

  const [schema, setSchema] = useState<SchemaDoc | null>(null);
  const [participant, setParticipant] = useState<ParticipantDoc | null>(null);
  const [draft, setDraft] = useState<FieldDraft>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [ttlSeconds, setTtlSeconds] = useState<number | ''>('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<QrGenerateResponse | null>(null);
  const [genError, setGenError] = useState<string | null>(null);

  useEffect(() => {
    if (!industryId || !schemaId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const s = await apiCall<SchemaDoc>(
          `/api/schemas/${schemaId}?industryId=${encodeURIComponent(industryId)}`,
        );
        if (cancelled) return;
        setSchema(s);
        setDraft(emptyDraft(s));
        // Pre-fill TTL from schema default.
        if (s.defaultCodeTtlDays && s.defaultCodeTtlDays > 0) {
          setTtlSeconds(s.defaultCodeTtlDays * 86400);
        }
        if (participantId) {
          const plist = await apiCall<ParticipantDoc[]>(
            `/api/schemas/${schemaId}/participants?industryId=${encodeURIComponent(industryId)}`,
          );
          if (cancelled) return;
          const match = plist.find((p) => p.id === participantId) ?? null;
          setParticipant(match);
          if (!match) {
            setError('Participant not found for this schema.');
          }
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : (e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [industryId, schemaId, participantId]);

  function setField(name: string, value: FieldValue) {
    setDraft((prev) => ({ ...prev, [name]: value }));
  }

  const downloadName = useMemo(() => {
    if (participant?.humanReadableId) {
      return `qr-${participant.humanReadableId.replace(/[^a-z0-9_-]+/gi, '_')}.png`;
    }
    return `qr-${Date.now()}.png`;
  }, [participant]);

  const expiresAtPreview = useMemo(() => {
    if (typeof ttlSeconds !== 'number' || ttlSeconds <= 0) return null;
    return new Date(Date.now() + ttlSeconds * 1000);
  }, [ttlSeconds]);

  async function generate() {
    if (!industryId || !schemaId || !schema) return;
    setGenError(null);

    let body: Record<string, unknown>;
    if (participantId) {
      body = { industryId, schemaId, participantId };
    } else {
      for (const p of schema.properties) {
        if (p.required) {
          const v = draft[p.name];
          if (v === '' || v === null || v === undefined) {
            setGenError(`"${p.label}" is required.`);
            return;
          }
        }
      }
      const fields: Record<string, FieldValue> = {};
      for (const p of schema.properties) {
        const raw = draft[p.name] ?? null;
        if (p.type === 'number') {
          fields[p.name] = raw === '' || raw === null ? null : Number(raw);
        } else if (p.type === 'boolean') {
          fields[p.name] = Boolean(raw);
        } else {
          fields[p.name] = raw === '' ? null : raw;
        }
      }
      body = { industryId, schemaId, fields };
    }
    if (typeof ttlSeconds === 'number' && ttlSeconds > 0) {
      body.ttlSeconds = ttlSeconds;
    }

    setGenerating(true);
    try {
      const r = await apiCall<QrGenerateResponse>('/api/qr/generate', { method: 'POST', body });
      setResult(r);
    } catch (e) {
      setGenError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setGenerating(false);
    }
  }

  function downloadPng() {
    if (!result) return;
    const binary = atob(result.pngBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'image/png' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  if (!industryId || !schemaId) {
    return <p style={{ color: 'var(--danger-color)' }}>Missing route parameters.</p>;
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Link
          to={`/industries/${industryId}/schemas/${schemaId}/participants`}
          style={{ color: 'var(--text-muted)', fontSize: 13, textDecoration: 'none' }}
        >
          ← Participants
        </Link>
        <h1 style={{ margin: '4px 0 0' }}>Generate QR</h1>
        {schema && (
          <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '4px 0 0' }}>
            {schema.name} · v{schema.version}
          </p>
        )}
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fee', border: '1px solid #fcc', borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : !schema ? null : (
        <div style={{ display: 'grid', gridTemplateColumns: result ? '1fr 1fr' : '1fr', gap: 24, alignItems: 'start' }}>
          <div
            style={{
              border: '1px solid var(--border-color)',
              background: 'var(--card-bg)',
              padding: 20,
              borderRadius: 8,
            }}
          >
            {participantId && participant ? (
              <>
                <h3 style={{ marginTop: 0 }}>Participant</h3>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0 }}>
                  ID: <strong style={{ color: 'var(--text-color)' }}>{participant.humanReadableId ?? participant.id}</strong>
                </p>
                <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', fontSize: 14 }}>
                  {schema.properties.slice(0, 6).map((p) => (
                    <ReadonlyRow key={p.name} label={p.label} value={participant.fields[p.name]} />
                  ))}
                </dl>
              </>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>Fields</h3>
                {schema.properties.map((p) => (
                  <div key={p.name} style={{ marginBottom: 12 }}>
                    <label style={labelStyle}>
                      {p.label}
                      {p.required ? ' *' : ''}
                    </label>
                    <FieldInput property={p} value={draft[p.name]} onChange={(v) => setField(p.name, v)} />
                  </div>
                ))}
              </>
            )}

            <div style={{ marginTop: 16 }}>
              <label style={labelStyle}>TTL (seconds)</label>
              <input
                type="number"
                min={0}
                value={ttlSeconds}
                onChange={(e) => setTtlSeconds(e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))}
                style={inputStyle}
                placeholder="(no expiry)"
              />
              <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '4px 0 0' }}>
                {expiresAtPreview
                  ? `These codes will stop working on ${expiresAtPreview.toLocaleString()}.`
                  : 'Leave blank for no expiry.'}
              </p>
            </div>

            {genError && (
              <div
                style={{
                  padding: 12,
                  background: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: 6,
                  margin: '12px 0',
                }}
              >
                {genError}
              </div>
            )}

            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void generate()}
              disabled={generating || (Boolean(participantId) && !participant)}
              style={{ marginTop: 12 }}
            >
              {generating ? 'Generating…' : 'Generate QR'}
            </button>
          </div>

          {result && (
            <div
              style={{
                border: '1px solid var(--border-color)',
                background: 'var(--card-bg)',
                padding: 20,
                borderRadius: 8,
              }}
            >
              <h3 style={{ marginTop: 0 }}>QR Code</h3>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <img
                  src={result.dataUri}
                  alt="Generated QR"
                  width={320}
                  height={320}
                  style={{ width: 320, height: 320, imageRendering: 'pixelated', background: '#fff', padding: 8, borderRadius: 4 }}
                />
              </div>
              <button type="button" className="btn btn--primary" onClick={downloadPng}>
                Download PNG
              </button>
              {result.expiresAt && (
                <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 16 }}>
                  Expires {new Date(result.expiresAt).toLocaleString()}
                </p>
              )}
              <h4 style={{ marginBottom: 4, marginTop: 24 }}>Encoded payload</h4>
              <pre
                style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 4,
                  padding: 12,
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  color: 'var(--text-muted)',
                  margin: 0,
                }}
              >
                {result.scanString}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReadonlyRow({ label, value }: { label: string; value: FieldValue | undefined }) {
  return (
    <>
      <dt style={{ color: 'var(--text-muted)' }}>{label}</dt>
      <dd style={{ margin: 0 }}>{formatFieldValue(value)}</dd>
    </>
  );
}

function formatFieldValue(v: FieldValue | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

function FieldInput({
  property,
  value,
  onChange,
}: {
  property: SchemaProperty;
  value: FieldValue | undefined;
  onChange: (v: FieldValue) => void;
}) {
  switch (property.type) {
    case 'number':
      return (
        <input
          type="number"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
    case 'date':
      return (
        <input
          type="date"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
    case 'boolean':
      return (
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
      );
    case 'select':
      return (
        <select
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        >
          <option value="">— Select —</option>
          {(property.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    default:
      return (
        <input
          type="text"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
  }
}
